import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { renderToStaticMarkup } from 'react-dom/server';

import { COUNTRY, locales } from '~globals';
import { useTranslation } from 'react-i18next';
import { convertBlogCategoryToSlug } from '~build/utils';
import { Breadcrumb } from '~build/models';
import { ContentfulBlogPost, CoverageLandingPageCommon } from '~models/contentful';
import insifyLogo from '~assets/svg/insify-logo-wood-90.svg?path';

import type { BaseFaqQuestion } from '~components/BaseFaq';
import type { ReactElement } from 'react';
import type { TFunction } from 'i18next';

type FAQStucturedData = {
  '@context': 'https://schema.org';
  '@type': 'FAQPage';
  mainEntity: MainEntityFAQ[];
};

type MainEntityFAQ = {
  '@type': 'Question';
  name: string;
  acceptedAnswer: {
    '@type': 'Answer';
    text: string;
  };
};

type CoverageLandingPageCommonWithPath = CoverageLandingPageCommon & { path: string; breadcrumbs: Breadcrumb[] };

type UseStructuredData = {
  getHomePageStructuredData: (breadcrumbs: Breadcrumb[]) => string[];
  getLandingPageStructuredData: (page: CoverageLandingPageCommonWithPath) => string[];
  getBlogPageStructuredData: (page: ContentfulBlogPost) => string[];
  getFAQStructuredData: (questions: BaseFaqQuestion[]) => FAQStucturedData;
  insuranceAgency: Record<string, string | unknown>;
  blogStructuredData: Record<string, string>;
};

const phoneByCountry = {
  nl: '+31208881089',
  fr: '+31208881089',
  de: '+496994323111',
};

const structuredData = {
  home: (breadcrumbs: Breadcrumb[], t: TFunction) => {
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'InsuranceAgency',
        name: 'Insify',
        description: t('common:seo_description'),
        image: `https://www.insify.${COUNTRY}/og-image.jpg`,
        address: {
          '@type': 'PostalAddress',
          streetAddress: t('common:seo_street_address'),
          addressLocality: t('common:seo_address_locality'),
          postalCode: t('common:seo_postal_code'),
          addressCountry: t('common:seo_address_country'),
        },
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer support',
          email: t('common:seo_email'),
          telephone: t('common:seo_telephone'),
          hoursAvailable: 'Mo-Fr 09:00-18:00',
        },
      },
      {
        '@context': 'https://schema.org/',
        '@type': 'BreadcrumbList',
        '@id': `https://www.insify.${COUNTRY}/#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: breadcrumbs[0].label,
            item: `https://www.insify.${COUNTRY}/`,
          },
        ],
      },
    ];
  },
  landing: (
    { createdAt, updatedAt, metaFields, path, breadcrumbs }: CoverageLandingPageCommonWithPath,
    t: TFunction,
  ) => {
    const domain = `https://www.insify.${COUNTRY}`;
    const domainWithSlug = domain + path;

    return [
      {
        '@context': 'https://schema.org',
        '@type': 'webPage',
        '@id': `${domainWithSlug}#webpage`,
        url: domainWithSlug,
        name: metaFields.openGraphTitle || metaFields.metaTitle,
        description: metaFields.openGraphDescription || metaFields.metaDescription,
        inLanguage: locales[COUNTRY],
        datePublished: createdAt,
        dateModified: updatedAt,
        primaryImageOfPage: {
          '@type': 'ImageObject',
          '@id': `${domainWithSlug}#primaryimage`,
          inLanguage: locales[COUNTRY],
          url: metaFields.metaImage?.file.url || '',
          contentUrl: metaFields.metaImage?.file.url || '',
        },
        isPartOf: {
          '@type': 'Website',
          '@id': `${domain}/#website`,
          url: domain,
          name: 'Insify',
          description: t('common:seo_description'),
          inLanguage: locales[COUNTRY],
        },
        potentialAction: {
          '@type': 'ReadAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: domainWithSlug,
          },
        },
      },
      {
        '@context': 'https://schema.org/',
        '@type': 'BreadcrumbList',
        '@id': `${domainWithSlug}#breadcrumb`,
        itemListElement: breadcrumbs.map((breadcrumb, index) => ({
          '@type': 'ListItem',
          position: ++index,
          name: breadcrumb.label,
          item: `${domain}${breadcrumb.path}`,
        })),
      },
    ];
  },
  blog: (page: ContentfulBlogPost, t: TFunction) => {
    const domain = `https://www.insify.${COUNTRY}`;
    const domainWithSlug = `${domain}/blog${convertBlogCategoryToSlug(page.category)}/${page.slug}`;

    return [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        '@id': `${domainWithSlug}/#article`,
        headline: page.title,
        description: page.excerpt,
        inLanguage: locales[COUNTRY],
        datePublished: page.publicationDate,
        dateModified: page.updatedAt,
        articleBody: renderToStaticMarkup(documentToReactComponents(JSON.parse(page.content.raw)) as ReactElement),
        url: domainWithSlug,
        image: {
          '@type': 'ImageObject',
          '@id': `${domainWithSlug}/#image`,
          inLanguage: locales[COUNTRY],
          url: page.socialMediaImage.file.url,
          width: page.socialMediaImage.file.details.image.width,
          height: page.socialMediaImage.file.details.image.height,
        },
        publisher: {
          '@type': 'Organization',
          '@id': `${domain}/#publisher`,
          name: 'Insify',
          logo: {
            '@type': 'ImageObject',
            url: insifyLogo,
            width: 120,
            height: 28,
          },
        },
        author: {
          '@type': 'Person',
          '@id': `${domainWithSlug}/#author`,
          name: page.author,
          image: {
            '@type': 'ImageObject',
            url: page.authorImage?.file.url,
          },
        },
        isPartOf: {
          '@type': 'Website',
          '@id': `${domain}/#website`,
          url: domain,
          name: 'Insify',
          description: t('common:seo_description'),
          inLanguage: locales[COUNTRY],
        },
        potentialAction: {
          '@type': 'ReadAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: domainWithSlug,
          },
        },
      },
    ];
  },
};

export const useStructuredData = (): UseStructuredData => {
  const { t } = useTranslation(['common']);

  const getHomePageStructuredData = (breadcrumbs: Breadcrumb[]): string[] => {
    return structuredData.home(breadcrumbs, t).map((chunk) => JSON.stringify(chunk));
  };

  const getLandingPageStructuredData = (page: CoverageLandingPageCommonWithPath): string[] => {
    return structuredData.landing(page, t).map((chunk) => JSON.stringify(chunk));
  };

  const getBlogPageStructuredData = (page: ContentfulBlogPost): string[] => {
    return structuredData.blog(page, t).map((chunk) => JSON.stringify(chunk));
  };

  const insuranceAgency = {
    '@context': 'https://schema.org',
    '@type': 'InsuranceAgency',
    name: 'Insify',
    description: t('common:seo_description'),
    openingHours: 'Mo-Fr 09:00-18:00',
    telephone: phoneByCountry[COUNTRY],
    image: `${process.env.GATSBY_SITE_URL}/og-image.jpg`,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Amsterdam, Netherlands',
      postalCode: '1018 XA',
      streetAddress: 'Weesperplein 4B',
    },
    currenciesAccepted: 'EUR',
  };

  const blogStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
  };

  const mapToMainEntityFAQ = (questions: BaseFaqQuestion[]): MainEntityFAQ[] => {
    return questions?.map(
      ({ question, answer }: BaseFaqQuestion): MainEntityFAQ => ({
        '@type': 'Question',
        name: question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: renderToStaticMarkup(documentToReactComponents(JSON.parse(answer)) as ReactElement),
        },
      }),
    );
  };

  const getFAQStructuredData = (questions: BaseFaqQuestion[]): FAQStucturedData => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: mapToMainEntityFAQ(questions),
  });

  return {
    getHomePageStructuredData,
    getLandingPageStructuredData,
    getBlogPageStructuredData,
    getFAQStructuredData,
    insuranceAgency,
    blogStructuredData,
  };
};
